import React, { useEffect, useMemo, useState, useContext } from 'react';
import {
  Avatar,
  Box,
  Text,
  Image,
  Flex,
  Link,
  Grid,
  Spinner,
  Button,
  HStack,
  VStack,
  useDisclosure,
  IconButton,
  SimpleGrid,
} from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { Client, Campaign, FoodfluenceLocation } from '@foodfluence/shared';
import CampaignComponent from '../components/CampaignComponent';
import RequestCollabModal from '../components/RequestCollabModal';
import LocationList from '../../../components/LocationList';
import { exploreApi } from '../../../services/api/explore';
import SignInSignUpModal from '../components/SignInSignUpModal';
import { useAuth } from '../../../contexts/AuthContext';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { getClientIdFromCollabRequest } from 'utils/collabUtils';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import PitchCollabSheet, { PitchType } from '../components/PitchCollabSheet';
import { ff } from '../../../theme';
import MarkdownText from '../../../components/MarkdownText';

import { WhiteLabelContext } from '../../../contexts/WhiteLabelContext';
import { SINGLE_CLIENT_COLLAB_REQUEST_LIMIT, filterAvailableCampaignsForClient, getBrandImage } from '../utils';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { captureInboundLinkToken } from 'utils/inboundLinkUtils';
import { useInfluencerLocations } from '../../../contexts/InfluencerLocationContext';
import { calculateDistance, getInfluencerPrimaryLocation } from '../../../utils/locationUtils';

/** Brands can have dozens of stores; the page lists the nearest few and expands on request. */
const NEAREST_LOCATION_COUNT = 3;

export interface ClientWithCampaigns extends Client {
  campaigns: (Campaign & { locations: FoodfluenceLocation[] })[];
  locations?: FoodfluenceLocation[];
}

const ClientPage: React.FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { isWhiteLabeled, clientId: whiteLabelClientId } = useContext(WhiteLabelContext);
  const [client, setClient] = useState<ClientWithCampaigns | null>(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isRequestModalOpen, onOpen: onRequestModalOpen, onClose: onRequestModalClose } = useDisclosure();
  const { isOpen: isPitchSheetOpen, onOpen: onPitchSheetOpen, onClose: onPitchSheetClose } = useDisclosure();
  const [pitchType, setPitchType] = useState<PitchType | undefined>();
  const { currentUser } = useAuth();
  const { influencerData } = useContext(InfluencerDataContext);
  const { influencerLocations } = useInfluencerLocations();
  const [showAllLocations, setShowAllLocations] = useState(false);
  const nearLocation = getInfluencerPrimaryLocation(influencerLocations);
  const sortedLocations = useMemo(() => {
    const locations = client?.locations ?? [];
    if (!nearLocation) return locations;
    const miles = (loc: { lat: number; lng: number }) => calculateDistance(nearLocation.lat, nearLocation.lng, loc.lat, loc.lng);
    return [...locations].sort((a, b) => miles(a) - miles(b));
  }, [client, nearLocation]);
  const collabData = useContext(CollabDataContext);
  const campaigns = useContext(InfluencerCampaignDataContext);
  const [searchParams] = useSearchParams();
  // If white-labeled, override the clientId from params
  const effectiveClientId = isWhiteLabeled ? whiteLabelClientId : clientId;

  const pendingRequests = collabData.filter(collab => 
    collab.status === 'pendingClientApproval' &&  

    getClientIdFromCollabRequest(collab, campaigns?.[collab.id]) === effectiveClientId
  );

  // Hide back button and prevent navigation if white-labeled
  const showBackButton = !isWhiteLabeled && currentUser;
  // The creator picks UGC vs influencer first; signed-out creators carry the pick through sign-in
  const handlePitchSelect = (type: PitchType) => {
    setPitchType(type);
    onPitchSheetClose();

    if (!currentUser) {
      onOpen();
      return;
    }

    // Open in place so closing returns the user to their scroll position
    onRequestModalOpen();
  };

  // The dashboard mints a signed token into the shared Creator Link's ?inbound= param;
  // stash it so the collab request can be tagged (and auto-approved) as inbound
  useEffect(() => {
    if (effectiveClientId) {
      captureInboundLinkToken(effectiveClientId, searchParams);
    }
  }, [effectiveClientId, searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!effectiveClientId) return;
        const data = await exploreApi.getClientAndCampaigns({ clientId: effectiveClientId });
        if (data) {
          setClient({ ...data.client, campaigns: data.campaigns });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveClientId]);

  if (loading) {
    return (
      <Flex height="100vh" align="center" justify="center">
        <Spinner size="xl" color={ff.ink} />
      </Flex>
    );
  }

  const influencerId = influencerData?.id;
  const isMutedForInfluencer = !!influencerId && !!client?.mutedInfluencers?.includes(influencerId);

  if (!client || isMutedForInfluencer) {
    return (
      <Box px="20px" pt="24px" fontFamily="display" color={ff.ink}>
        <Text fontSize="15px">Client not found</Text>
      </Box>
    );
  }

  const availableCampaigns = filterAvailableCampaignsForClient<Campaign & { locations: FoodfluenceLocation[] }>(
    client.campaigns,
    collabData,
    client.id,
    influencerData
  );
  const canPitch = pendingRequests.length < SINGLE_CLIENT_COLLAB_REQUEST_LIMIT;
  const socials = [
    client.instagramHandle && { label: 'Instagram', href: `https://instagram.com/${client.instagramHandle}` },
    client.tiktokHandle && { label: 'TikTok', href: `https://tiktok.com/@${client.tiktokHandle}` },
    client.website && { label: 'Website', href: client.website },
  ].filter((s): s is { label: string; href: string } => !!s);
  // Board line is "Category · City"; clients have no category field, so only the first location's city shows
  const city = client.locations?.[0]?.city;

  const pitchButton = (
    <Button
      h="56px"
      w="100%"
      borderRadius="18px"
      bg={ff.lime}
      color={ff.ink}
      fontSize="16px"
      fontWeight={600}
      lineHeight="normal"
      _hover={{ bg: ff.lime }}
      _active={{ bg: ff.lime }}
      onClick={onPitchSheetOpen}
    >
      Pitch a collab
    </Button>
  );

  const sectionTitle = (title: string) => (
    <Text as="h2" textStyle="eyebrow" fontSize="13px" letterSpacing="0.08em" m={0}>
      {title}
    </Text>
  );

  return (
    <Box minH="100vh" bg="white" fontFamily="display" color={ff.ink}>
      <Box position="relative" h="260px" bg="#3A2E2A">
        <Image
          src={getBrandImage(null, client)}
          alt=""
          objectFit="cover"
          w="100%"
          h="100%"
        />
        {showBackButton && (
          <IconButton
            icon={<FiChevronLeft size={20} strokeWidth={2.2} />}
            aria-label="Back to Explore"
            onClick={() => navigate('/explore')}
            position="absolute"
            top="16px"
            left="16px"
            w="44px"
            h="44px"
            minW="44px"
            borderRadius="full"
            bg="white"
            color={ff.ink}
            _hover={{ bg: ff.grey }}
          />
        )}
      </Box>

      <VStack px="20px" pt="22px" pb="48px" spacing="22px" align="stretch">
        <HStack spacing="14px" align="center">
          <Avatar
            src={client.instagramProfilePicture}
            name={client.accountName}
            boxSize="60px"
            bg={ff.grey}
            color={ff.text2}
            flex="none"
            sx={{ '& > [role=img]': { fontSize: '13px', fontWeight: 600 } }}
          />
          <VStack align="flex-start" spacing="4px" minW={0}>
            <Text as="h1" m={0} textStyle="display" fontSize="34px" lineHeight="1.12" letterSpacing="0">
              {client.accountName}
            </Text>
            {city && (
              <Text as="span" fontSize="14px" lineHeight="normal" color={ff.text2}>
                {city}
              </Text>
            )}
          </VStack>
        </HStack>

        {socials.length > 0 && (
          <Grid
            as="nav"
            aria-label="Brand socials"
            templateColumns={`repeat(${socials.length}, minmax(0, 1fr))`}
            border="1.5px solid"
            borderColor={ff.ink}
            borderRadius="999px"
            overflow="hidden"
          >
            {socials.map((social, i) => (
              <Link
                key={social.label}
                href={social.href}
                isExternal
                h="46px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="4px"
                borderLeft={i > 0 ? '1.5px solid' : undefined}
                borderColor={ff.ink}
                color={ff.ink}
                fontSize="14px"
                fontWeight={600}
                lineHeight="normal"
                _hover={{ textDecoration: 'none', color: ff.ink }}
                aria-label={`${social.label} (opens in a new tab)`}
              >
                {social.label} <span aria-hidden="true">↗</span>
              </Link>
            ))}
          </Grid>
        )}

        {client.description && (
          <MarkdownText fontSize="16px" lineHeight="1.5">
            {client.description}
          </MarkdownText>
        )}

        <VStack as="section" align="stretch" spacing="14px" pt="18px" borderTop="1px solid" borderColor={ff.ink}>
          {sectionTitle('Open campaigns')}
          {availableCampaigns.length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} rowGap="28px" columnGap="24px">
              {availableCampaigns.map(campaign => (
                <CampaignComponent key={campaign.id} campaign={campaign} client={client} />
              ))}
            </SimpleGrid>
          )}
          {availableCampaigns.length === 0 ? (
            <VStack align="stretch" spacing="14px">
              <Text textStyle="display" fontSize="29px" lineHeight="1.12" letterSpacing="0" m={0}>
                Nothing open right now
              </Text>
              <Text fontSize="15px" lineHeight="1.5" color={ff.body} m={0}>
                {canPitch
                  ? 'Pitch your own idea. They can accept, counter, or pass.'
                  : 'Your pitches to this brand are waiting on a reply.'}
              </Text>
              {canPitch && pitchButton}
            </VStack>
          ) : (
            canPitch && pitchButton
          )}
        </VStack>

        {pendingRequests.length > 0 && (
          <VStack as="section" align="stretch" spacing="14px" pt="18px" borderTop="1px solid" borderColor={ff.line}>
            {sectionTitle('Pending requests')}
            <VStack align="stretch" spacing="28px">
              {pendingRequests.map((collab) => (
                // A campaign that left the live list (e.g. its go-live can no longer be met) still owns the
                // request: fall back to the by-ID copy so editing it isn't treated as custom.
                <CampaignComponent
                  key={collab.id}
                  campaign={
                    client.campaigns.find(campaign => campaign.id === collab.influencerCampaignID) ??
                    (campaigns[collab.id] && { ...campaigns[collab.id], locations: [] })!
                  }
                  client={client}
                  requestedCollab={collab}
                />
              ))}
            </VStack>
          </VStack>
        )}

        {(client.locations?.length ?? 0) > 0 && (
          <VStack as="section" align="stretch" spacing="14px" pt="18px" borderTop="1px solid" borderColor={ff.line}>
            {sectionTitle('Locations')}
            <LocationList
              locations={showAllLocations ? sortedLocations : sortedLocations.slice(0, NEAREST_LOCATION_COUNT)}
              title=""
            />
            {sortedLocations.length > NEAREST_LOCATION_COUNT && (
              <Button
                variant="unstyled"
                alignSelf="flex-start"
                h="32px"
                minW="auto"
                lineHeight="normal"
                fontSize="14px"
                fontWeight={600}
                color={ff.ink}
                textDecoration="underline"
                onClick={() => setShowAllLocations(open => !open)}
              >
                {showAllLocations ? 'Show fewer' : `Show all ${sortedLocations.length} locations`}
              </Button>
            )}
          </VStack>
        )}
      </VStack>

      <PitchCollabSheet
        isOpen={isPitchSheetOpen}
        onClose={onPitchSheetClose}
        brandName={client.accountName}
        onSelect={handlePitchSelect}
      />
      <SignInSignUpModal
        isOpen={isOpen}
        onClose={onClose}
        returnTo={isWhiteLabeled ? '/request-collab' : `/explore/client/${effectiveClientId}/request-collab`}
        returnState={{
          client: client,
          pitchType,
        }}
      />
      <RequestCollabModal
        isOpen={isRequestModalOpen}
        onClose={onRequestModalClose}
        client={client}
        pitchType={pitchType}
      />
    </Box>
  );
};

export default ClientPage;
